<template>
  <div v-if="!!totalPages" class="pagination-container">
    <button type="button" @click="goToFirstPage()">&lt;&lt;</button>
    <button type="button" @click="goBack()">&lt;</button>

    <button
      v-for="page in pagesToDisplay"
      :key="`page-${page}`"
      :class="{ active: page === currentPage }"
      @click="changePage(page)"
    >
      {{ page }}
    </button>

    <button type="button" @click="goNext()">&gt;</button>
    <button type="button" @click="goToLastPage()">&gt;&gt;</button>
  </div>
</template>

<script>
const FIRST_PAGE = 1
const DEFAULT_PER_PAGE = 8
const MAX_PAGES_TO_DISPLAY = 5
const PAGE_CENTERING_OFFSET = 2

export default {
  props: {
    itens_per_page: {
      required: false,
      type: Number,
    },

    items: {
      required: true,
      type: Array,
    },

    update_visible_items: {
      required: true,
      type: Function,
    },
  },

  data() {
    return {
      currentPage: FIRST_PAGE,
      totalPages: 0,
      pagesToDisplay: [],
      itemsPerPage: DEFAULT_PER_PAGE,
    }
  },

  watch: {
    items() {
      if (this.currentPage === FIRST_PAGE) return this.updatePagination()

      this.currentPage = FIRST_PAGE
    },

    currentPage() {
      this.updatePagination()
    },
  },

  methods: {
    goToFirstPage() {
      this.currentPage = 1
    },

    goBack() {
      if (this.currentPage === FIRST_PAGE) return

      this.currentPage = this.currentPage - 1
    },

    goNext() {
      if (this.currentPage === this.totalPages) return

      this.currentPage = this.currentPage + 1
    },

    goToLastPage() {
      this.currentPage = this.totalPages
    },

    updateTotalPages() {
      const totalPages = Math.ceil(this.items.length / this.itemsPerPage)
      this.totalPages = totalPages
    },

    changePage(page) {
      this.currentPage = page
    },

    determineMiddlePage() {
      const hasEnoughToLeft = this.currentPage - PAGE_CENTERING_OFFSET >= FIRST_PAGE
      const hasEnoughToRight = this.currentPage + PAGE_CENTERING_OFFSET <= this.totalPages

      const canNaturallyBeInMiddle = hasEnoughToLeft && hasEnoughToRight

      if (canNaturallyBeInMiddle) return this.currentPage

      const leftDistance = this.currentPage - FIRST_PAGE
      const rightDistance = this.totalPages - this.currentPage

      const leftIsMissing = rightDistance < leftDistance
      const offsetToAdjust = leftIsMissing ? rightDistance : leftDistance

      const middle = leftIsMissing
        ? this.currentPage + offsetToAdjust
        : this.currentPage - offsetToAdjust

      return middle
    },

    getItemsToDisplay() {
      const INDEX_OFFSET = 1

      const pageIndex = this.currentPage - INDEX_OFFSET

      const startIndex = pageIndex * this.itemsPerPage
      const endIndex = this.currentPage * this.itemsPerPage

      return [startIndex, endIndex]
    },

    updateItemsToDisplay() {
      this.update_visible_items(this.getItemsToDisplay())
    },

    createPagination(from, to) {
      const FIRST_PAGE_OFFSET = 1
      const total = to - from + FIRST_PAGE_OFFSET

      const pages = Array.from({ length: total }, (_, index) => index + 1)

      return pages
    },

    getPagesToDisplay() {
      if (this.totalPages <= MAX_PAGES_TO_DISPLAY)
        return this.createPagination(FIRST_PAGE, this.totalPages)

      const middlePage = this.determineMiddlePage()

      return this.createPagination(
        middlePage - PAGE_CENTERING_OFFSET,
        middlePage + PAGE_CENTERING_OFFSET,
      )
    },

    updatePagination() {
      this.updateTotalPages()

      this.pagesToDisplay = this.getPagesToDisplay()

      this.updateItemsToDisplay()
    },
  },

  created() {
    if (this.itens_per_page) this.itemsPerPage = this.itens_per_page

    this.updatePagination()
  },
}
</script>

<style lang="scss" scoped>
.pagination-container {
  width: fit-content;
  margin: 0 auto;
}

button {
  padding: 6px 12px;
  font-size: 0.9rem;
  @include transparentButton;
  display: inline-flex;
  color: #666;

  &.active {
    color: $blue;
    background-color: $white;
  }

  &:not(.active):hover {
    background-color: #eee;
  }
}
</style>
